import React, { useCallback, useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Heart, Send, Trash2, Loader2 } from "lucide-react";
import { Avatar } from "@/components/community/ChatList";
import { MentionInput, RichText } from "@/components/feed/MentionInput";

/** Comentarios con respuestas y "me gusta". */
export const Comments = ({ postId, me, onCountChange }) => {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await userApi.get(`/feed/${postId}/comments`);
      setItems(data.comments);
    } catch (e) { toast.error(apiError(e)); setItems([]); }
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await userApi.post(`/feed/${postId}/comments`,
        { text: text.trim(), reply_to: replyTo, mentions });
      setText(""); setReplyTo(null); setMentions([]);
      await load();
      onCountChange?.();
    } catch (e) { toast.error(apiError(e, "No se pudo comentar")); }
    finally { setBusy(false); }
  };

  const like = async (c) => {
    try {
      const { data } = await userApi.post(`/feed/comments/${c.comment_id}/like`);
      setItems((list) => list.map((x) => x.comment_id === c.comment_id
        ? { ...x, i_like: data.i_like, likes: data.likes } : x));
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    try {
      await userApi.delete(`/feed/comments/${c.comment_id}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const tree = (items || []).filter((c) => !c.reply_to);
  const repliesOf = (id) => (items || []).filter((c) => c.reply_to === id);

  const Row = ({ c, nested }) => (
    <div className={`flex gap-2.5 ${nested ? "ml-10" : ""}`} data-testid={`comment-${c.comment_id}`}>
      <Avatar person={c.author} size={nested ? 26 : 32} />
      <div className="min-w-0 flex-1">
        <div className="chat-bubble px-3 py-2">
          <div className="text-[11px] text-white/55">{c.author?.name}</div>
          <div className="text-sm text-white whitespace-pre-wrap break-words">
            {c.deleted ? <span className="italic text-white/40">Comentario eliminado</span> : <RichText text={c.text} />}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-white/45">
          <button data-testid={`comment-like-${c.comment_id}`} onClick={() => like(c)}
            className={`flex items-center gap-1 ${c.i_like ? "text-red-400" : "hover:text-white"}`}>
            <Heart className={`w-3 h-3 ${c.i_like ? "fill-current" : ""}`} /> {c.likes || ""}
          </button>
          {!nested && (
            <button data-testid={`comment-reply-${c.comment_id}`}
              onClick={() => setReplyTo(c.comment_id)} className="hover:text-white">Responder</button>
          )}
          {(c.author_key === me?.key || me?.is_admin) && !c.deleted && (
            <button data-testid={`comment-delete-${c.comment_id}`} onClick={() => remove(c)}
              className="hover:text-rose-300 flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
          )}
          <span className="font-mono">{(c.created_at || "").slice(11, 16)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-3 pt-3 border-t border-white/8 space-y-3" data-testid={`comments-${postId}`}>
      {items === null ? <Loader2 className="w-4 h-4 animate-spin text-red-400" /> : (
        <div className="space-y-3">
          {tree.map((c) => (
            <div key={c.comment_id} className="space-y-2">
              <Row c={c} />
              {repliesOf(c.comment_id).map((r) => <Row key={r.comment_id} c={r} nested />)}
            </div>
          ))}
          {tree.length === 0 && <div className="text-xs text-white/45">Sé el primero en comentar.</div>}
        </div>
      )}

      <div className="flex items-end gap-2">
        <Avatar person={me} size={30} />
        <MentionInput dropUp testId={`comment-input-${postId}`} value={text} onChange={setText}
          onMentions={setMentions} onEnter={send} className="text-sm py-2"
          placeholder={replyTo ? "Escribe tu respuesta…" : "Comenta y usa @ para etiquetar"} />
        <button data-testid={`comment-send-${postId}`} onClick={send} disabled={busy}
          className="glass-btn glass-btn-red w-9 h-9 rounded-xl flex items-center justify-center shrink-0">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default Comments;
